// frontend/src/services/titleService.js
// Enterprise-grade title generation service with user edit protection and intelligent fallbacks

import { markdownService } from './markdownService';

/**
 * Title generation service with user edit protection
 * Handles automatic title generation while respecting user intentions
 */
class TitleService {
  constructor() {
    this.titleLocks = new Map(); // Track which notes have user-edited titles
    this.titleHistory = new Map(); // Track title changes for undo functionality
  }

  /**
   * Generate title from content with user edit protection
   * @param {string} noteId - Unique note identifier
   * @param {string} content - Note content
   * @param {string} currentTitle - Current title value
   * @param {Object} options - Generation options
   * @returns {Object} Title generation result
   */
  generateTitle(noteId, content, currentTitle = '', options = {}) {
    const {
      respectUserEdits = true,
      forceGeneration = false,
      maxLength = 50,
      fallbackTitle = 'Untitled'
    } = options;

    try {
      // Check if user has manually edited this title
      const isLocked = this.isUserLocked(noteId);

      if (respectUserEdits && isLocked && !forceGeneration) {
        return {
          title: currentTitle,
          generated: false,
          locked: true,
          reason: 'user_edited'
        };
      }

      // Don't generate if title already exists and we're not forcing
      if (currentTitle && currentTitle.trim() !== '' && !forceGeneration) {
        return {
          title: currentTitle,
          generated: false,
          locked: false,
          reason: 'title_exists'
        };
      }

      // Generate title from content
      const generatedTitle = this.extractTitleFromContent(content, { maxLength });

      // Use fallback if generation failed
      const finalTitle = generatedTitle || fallbackTitle;

      // Store in history for potential undo
      this.addToHistory(noteId, currentTitle, finalTitle);

      return {
        title: finalTitle,
        generated: true,
        locked: false,
        reason: 'auto_generated',
        previous: currentTitle
      };

    } catch (error) {
      console.error('TitleService: Failed to generate title:', error);
      return {
        title: currentTitle || fallbackTitle,
        generated: false,
        locked: false,
        reason: 'error',
        error: error.message
      };
    }
  }

  /**
   * Extract title from various content types
   * @private
   * @param {string} content - Content to extract title from
   * @param {Object} options - Extraction options
   * @returns {string} Extracted title
   */
  extractTitleFromContent(content, options = {}) {
    if (!content || typeof content !== 'string') {
      return '';
    }

    const { maxLength = 50 } = options;

    // Handle different content types
    if (this.isHtml(content)) {
      return this.extractFromHtml(content, maxLength);
    } else if (markdownService.isMarkdown(content)) {
      return this.extractFromMarkdown(content, maxLength);
    } else {
      return this.extractFromPlainText(content, maxLength);
    }
  }

  /**
   * Extract title from HTML content
   * @private
   */
  extractFromHtml(html, maxLength) {
    try {
      // Create a temporary DOM element to parse HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      // Look for heading elements first
      const headings = tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6');
      if (headings.length > 0) {
        const title = headings[0].textContent.trim();
        return this.truncateTitle(title, maxLength);
      }

      // Fall back to first paragraph or any text
      const firstP = tempDiv.querySelector('p');
      if (firstP) {
        const title = firstP.textContent.trim();
        return this.truncateTitle(title, maxLength);
      }

      // Last resort: all text content
      const allText = tempDiv.textContent.trim();
      return this.truncateTitle(allText, maxLength);

    } catch (error) {
      console.error('TitleService: Failed to extract from HTML:', error);
      return '';
    }
  }

  /**
   * Extract title from markdown content
   * @private
   */
  extractFromMarkdown(markdown, maxLength) {
    return markdownService.extractTitle(markdown);
  }

  /**
   * Extract title from plain text content
   * @private
   */
  extractFromPlainText(text, maxLength) {
    const lines = text.split('\n').map(line => line.trim()).filter(Boolean);

    if (lines.length === 0) {
      return '';
    }

    const firstLine = lines[0];
    return this.truncateTitle(firstLine, maxLength);
  }

  /**
   * Truncate title to specified length with smart word boundaries
   * @private
   */
  truncateTitle(title, maxLength) {
    if (!title || title.length <= maxLength) {
      return title;
    }

    // Find the last space within the limit
    const truncated = title.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');

    if (lastSpace > maxLength * 0.7) { // At least 70% of desired length
      return truncated.substring(0, lastSpace).trim() + '...';
    } else {
      return truncated.trim() + '...';
    }
  }

  /**
   * Detect if content is HTML
   * @private
   */
  isHtml(content) {
    return /<[a-z][\s\S]*>/i.test(content);
  }

  /**
   * Lock title to prevent automatic generation
   * @param {string} noteId - Note identifier
   * @param {string} reason - Reason for locking
   */
  lockTitle(noteId, reason = 'user_edit') {
    this.titleLocks.set(noteId, {
      locked: true,
      reason,
      timestamp: Date.now()
    });
  }

  /**
   * Unlock title to allow automatic generation
   * @param {string} noteId - Note identifier
   */
  unlockTitle(noteId) {
    this.titleLocks.delete(noteId);
  }

  /**
   * Check if title is user-locked
   * @param {string} noteId - Note identifier
   * @returns {boolean} True if locked
   */
  isUserLocked(noteId) {
    return this.titleLocks.has(noteId);
  }

  /**
   * Get lock information for debugging
   * @param {string} noteId - Note identifier
   * @returns {Object|null} Lock information
   */
  getLockInfo(noteId) {
    return this.titleLocks.get(noteId) || null;
  }

  /**
   * Add title change to history
   * @private
   */
  addToHistory(noteId, oldTitle, newTitle) {
    if (!this.titleHistory.has(noteId)) {
      this.titleHistory.set(noteId, []);
    }

    const history = this.titleHistory.get(noteId);
    history.push({
      from: oldTitle,
      to: newTitle,
      timestamp: Date.now()
    });

    // Keep only last 10 changes
    if (history.length > 10) {
      history.shift();
    }
  }

  /**
   * Get title change history for a note
   * @param {string} noteId - Note identifier
   * @returns {Array} Title change history
   */
  getHistory(noteId) {
    return this.titleHistory.get(noteId) || [];
  }

  /**
   * Clear all locks and history (useful for testing)
   */
  reset() {
    this.titleLocks.clear();
    this.titleHistory.clear();
  }

  /**
   * Get service statistics
   * @returns {Object} Service statistics
   */
  getStats() {
    return {
      lockedTitles: this.titleLocks.size,
      notesWithHistory: this.titleHistory.size,
      totalHistoryEntries: Array.from(this.titleHistory.values())
        .reduce((sum, history) => sum + history.length, 0)
    };
  }
}

// Export singleton instance
export const titleService = new TitleService();

// Export class for testing
export { TitleService };

// Export commonly used functions
export const {
  generateTitle,
  lockTitle,
  unlockTitle,
  isUserLocked
} = titleService;